import { supabase } from '../lib/supabase';

/**
 * User Management Service
 * Handles all user-related operations for Super Admin
 */

export const userManagementService = {
  /**
   * Get all users with their company associations and roles
   */
  async getAllUsers() {
    try {
      // Get all user profiles
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select(`
          id,
          email,
          full_name,
          username,
          account_type,
          is_active,
          created_at,
          updated_at
        `)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Get all company_users associations
      const { data: companyUsers, error: cuError } = await supabase
        .from('company_users')
        .select(`
          user_id,
          company_id,
          role,
          company_alias,
          is_active,
          companies (
            id,
            company_name,
            company_tag,
            status
          )
        `);

      if (cuError) throw cuError;

      // Map company associations to users
      const usersWithCompanies = users.map(user => {
        const associations = companyUsers
          .filter(cu => cu.user_id === user.id)
          .map(cu => ({
            companyId: cu.company_id,
            companyName: cu.companies?.company_name,
            companyTag: cu.companies?.company_tag,
            companyStatus: cu.companies?.status,
            role: cu.role,
            companyAlias: cu.company_alias,
            isActive: cu.is_active
          }));

        return {
          id: user.id,
          email: user.email,
          fullName: user.full_name,
          username: user.username,
          accountType: user.account_type,
          isActive: user.is_active,
          createdAt: user.created_at,
          updatedAt: user.updated_at,
          companies: associations,
          companyCount: associations.length,
          isOrphaned: associations.length === 0 && user.account_type !== 'superadmin'
        };
      });

      return { success: true, data: usersWithCompanies };
    } catch (error) {
      console.error('Error fetching all users:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get orphaned users with detailed safety analysis
   */
  async getOrphanedUsers(minAgeHours = 1) {
    try {
      const { data, error } = await supabase.rpc('find_orphaned_users', {
        p_min_age_hours: minAgeHours
      });

      if (error) throw error;

      return {
        success: true,
        data: data || [],
        count: data?.length || 0,
        safeToDeleteCount: data?.filter(u => u.safe_to_delete).length || 0
      };
    } catch (error) {
      console.error('Error fetching orphaned users:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all data associated with a user (for deletion preview)
   */
  async getUserAssociatedData(userId) {
    try {
      const results = {
        userId,
        userProfile: null,
        companyAssociations: [],
        jobsCreated: [],
        jobInstancesAssigned: [],
        timesheets: [],
        notifications: [],
        notificationPreferences: [],
        pendingInvites: [],
        auditLogs: [],
        permissionOverrides: [],
        jobPreferences: []
      };

      // Fetch user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      results.userProfile = profile;

      // Fetch company associations
      const { data: companyUsers } = await supabase
        .from('company_users')
        .select('*, companies(company_name, company_tag)')
        .eq('user_id', userId);
      results.companyAssociations = companyUsers || [];

      // Fetch jobs created by user
      const { data: jobs } = await supabase
        .from('jobs')
        .select('id, job_name, job_type, status, created_at')
        .eq('created_by', userId);
      results.jobsCreated = jobs || [];

      // Fetch job instances assigned to user
      const { data: instances } = await supabase
        .from('job_instances')
        .select('id, scheduled_date, status, jobs(job_name)')
        .eq('assigned_to', userId);
      results.jobInstancesAssigned = instances || [];

      // Fetch timesheets
      const { data: timesheets } = await supabase
        .from('timesheets')
        .select('id, work_date, status, total_hours')
        .eq('contractor_id', userId);
      results.timesheets = timesheets || [];

      // Fetch notifications
      const { data: notifications } = await supabase
        .from('notifications')
        .select('id, type, title, created_at')
        .eq('user_id', userId);
      results.notifications = notifications || [];

      // Fetch notification preferences
      const { data: notifPrefs } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId);
      results.notificationPreferences = notifPrefs || [];

      // Fetch pending invites
      const { data: invites } = await supabase
        .from('pending_company_invites')
        .select('*, companies(company_name)')
        .eq('user_id', userId);
      results.pendingInvites = invites || [];

      // Fetch audit logs (entity_id or user_id matches)
      const { data: auditLogs } = await supabase
        .from('audit_logs')
        .select('id, action, entity_type, created_at, details')
        .or(`entity_id.eq.${userId},user_id.eq.${userId}`)
        .order('created_at', { ascending: false })
        .limit(50);
      results.auditLogs = auditLogs || [];

      // Fetch permission overrides
      const { data: permOverrides } = await supabase
        .from('user_permission_overrides')
        .select('*')
        .eq('user_id', userId);
      results.permissionOverrides = permOverrides || [];

      // Fetch job preferences
      const { data: jobPrefs } = await supabase
        .from('user_job_preferences')
        .select('*')
        .eq('user_id', userId);
      results.jobPreferences = jobPrefs || [];

      // Calculate totals
      results.totalRecords =
        (results.companyAssociations?.length || 0) +
        (results.jobsCreated?.length || 0) +
        (results.jobInstancesAssigned?.length || 0) +
        (results.timesheets?.length || 0) +
        (results.notifications?.length || 0) +
        (results.notificationPreferences?.length || 0) +
        (results.pendingInvites?.length || 0) +
        (results.permissionOverrides?.length || 0) +
        (results.jobPreferences?.length || 0);

      results.auditLogCount = results.auditLogs?.length || 0;

      return { success: true, data: results };
    } catch (error) {
      console.error('Error fetching user associated data:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a user and all associated data (except audit logs)
   */
  async deleteUserCompletely(userId, force = false) {
    try {
      // First, verify the user exists
      const { data: user, error: userError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, account_type')
        .eq('id', userId)
        .single();

      if (userError || !user) {
        return { success: false, error: 'User not found' };
      }

      // Prevent deleting superadmins
      if (user.account_type === 'superadmin') {
        return { success: false, error: 'Cannot delete superadmin users' };
      }

      // Check if user has company associations (if not forcing)
      if (!force) {
        const { data: companyUsers } = await supabase
          .from('company_users')
          .select('id')
          .eq('user_id', userId);

        if (companyUsers && companyUsers.length > 0) {
          return {
            success: false,
            error: 'User has company associations. Use force=true to delete anyway.',
            hasCompanies: true
          };
        }
      }

      // Log the deletion BEFORE deleting (so we have a record)
      await supabase.rpc('create_audit_log', {
        p_action: 'user.deleted_by_admin',
        p_entity_type: 'user',
        p_entity_id: userId,
        p_company_id: null,
        p_details: {
          email: user.email,
          full_name: user.full_name,
          account_type: user.account_type,
          deletion_type: force ? 'forced' : 'normal',
          deleted_data: 'all_except_audit_logs'
        }
      });

      // Delete in order of dependencies
      const deletions = [];

      // 1. Job instances assigned to user (unassign, don't delete the instance)
      const { error: instanceError } = await supabase
        .from('job_instances')
        .update({ assigned_to: null })
        .eq('assigned_to', userId);
      if (instanceError) deletions.push({ table: 'job_instances', error: instanceError.message });

      // 2. Timesheets
      const { error: timesheetError } = await supabase
        .from('timesheets')
        .delete()
        .eq('contractor_id', userId);
      if (timesheetError) deletions.push({ table: 'timesheets', error: timesheetError.message });

      // 3. Notifications
      const { error: notifError } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId);
      if (notifError) deletions.push({ table: 'notifications', error: notifError.message });

      // 4. Notification preferences
      const { error: notifPrefError } = await supabase
        .from('notification_preferences')
        .delete()
        .eq('user_id', userId);
      if (notifPrefError) deletions.push({ table: 'notification_preferences', error: notifPrefError.message });

      // 5. Pending invites
      const { error: inviteError } = await supabase
        .from('pending_company_invites')
        .delete()
        .eq('user_id', userId);
      if (inviteError) deletions.push({ table: 'pending_company_invites', error: inviteError.message });

      // 6. Permission overrides
      const { error: permError } = await supabase
        .from('user_permission_overrides')
        .delete()
        .eq('user_id', userId);
      if (permError) deletions.push({ table: 'user_permission_overrides', error: permError.message });

      // 7. Job preferences
      const { error: jobPrefError } = await supabase
        .from('user_job_preferences')
        .delete()
        .eq('user_id', userId);
      if (jobPrefError) deletions.push({ table: 'user_job_preferences', error: jobPrefError.message });

      // 8. Company users (associations)
      const { error: cuError } = await supabase
        .from('company_users')
        .delete()
        .eq('user_id', userId);
      if (cuError) deletions.push({ table: 'company_users', error: cuError.message });

      // 9. User profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', userId);
      if (profileError) deletions.push({ table: 'user_profiles', error: profileError.message });

      // 10. Auth user (via admin API - this might fail if not using service role)
      try {
        const { error: authError } = await supabase.auth.admin.deleteUser(userId);
        if (authError) deletions.push({ table: 'auth.users', error: authError.message });
      } catch (authErr) {
        deletions.push({ table: 'auth.users', error: 'Could not delete auth user - may require service role' });
      }

      if (deletions.length > 0) {
        return {
          success: true,
          partial: true,
          message: 'User deleted with some errors',
          errors: deletions,
          deletedUser: { id: userId, email: user.email, fullName: user.full_name }
        };
      }

      return {
        success: true,
        message: 'User and all associated data deleted successfully',
        deletedUser: { id: userId, email: user.email, fullName: user.full_name }
      };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Run orphan check and get notification data
   */
  async runOrphanCheck() {
    try {
      const { data, error } = await supabase.rpc('run_orphan_check');
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error running orphan check:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get orphan status for notifications
   */
  async getOrphanStatus() {
    try {
      const { data, error } = await supabase.rpc('get_orphan_status');
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error getting orphan status:', error);
      return { success: false, error: error.message };
    }
  }
};

export default userManagementService;
